import { useEffect, useState } from 'react';
import { Alert, Button, Card, DataGrid, useToast, type DataGridColumn } from '@kss/ui';
import { Check, X, RefreshCw, MessageSquare } from 'lucide-react';
import {
  workflowInstanceService,
  approvalLogService,
  type WorkflowInstance,
  type ApprovalAction,
} from '@kss/api';
import { useAuth } from '@kss/auth';

const STATUS_BADGE: Record<string, string> = {
  Pending:    'bg-warning/10 text-warning',
  InProgress: 'bg-primary/10 text-primary',
  Approved:   'bg-success/10 text-success',
  Rejected:   'bg-danger/10 text-danger',
  Cancelled:  'bg-content-muted/10 text-content-muted',
  Escalated:  'bg-danger/20 text-danger',
};

export default function ApprovalInbox() {
  const toast = useToast();
  const auth = useAuth();
  const [rows, setRows] = useState<WorkflowInstance[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setErr(null);
    try {
      const data = await workflowInstanceService.filter({ status: 'Pending' });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function act(row: WorkflowInstance, action: ApprovalAction) {
    if (!row.workflowInstanceId) return;
    const userId = auth.user?.id ?? '';
    if (!userId) {
      toast.error('Not signed in', 'Cannot record approval — no user id');
      return;
    }
    setBusyId(row.workflowInstanceId);
    try {
      await approvalLogService.create([
        {
          workflowInstanceId: row.workflowInstanceId,
          stepIndex: row.currentStepIndex,
          action,
          actorUserId: userId,
        },
      ]);
      toast.success(`${action}d`, `Step ${row.currentStepIndex} recorded`);
      await load();
    } catch (e) {
      toast.error('Failed', e instanceof Error ? e.message : 'Could not record action');
    } finally {
      setBusyId(null);
    }
  }

  const columns: DataGridColumn<WorkflowInstance>[] = [
    { accessor: 'entityType', title: 'Entity', sortable: true, width: 160 },
    { accessor: 'entityId', title: 'Reference', sortable: true, filterable: true },
    { accessor: 'currentStepIndex', title: 'Step', width: 70 },
    {
      accessor: 'status',
      title: 'Status',
      width: 120,
      render: (row: WorkflowInstance) => (
        <span className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${STATUS_BADGE[row.status] ?? ''}`}>
          {row.status}
        </span>
      ),
    },
    { accessor: 'initiatedBy', title: 'Initiated by', width: 160 },
    {
      accessor: 'initiatedAt',
      title: 'Submitted',
      width: 170,
      render: (row: WorkflowInstance) => (row.initiatedAt ? new Date(row.initiatedAt).toLocaleString() : '—'),
    },
    {
      accessor: 'workflowInstanceId',
      title: 'Action',
      width: 240,
      render: (row: WorkflowInstance) => (
        <div className="flex flex-wrap gap-1">
          <Button
            size="sm"
            variant="primary"
            disabled={busyId === row.workflowInstanceId}
            onClick={() => act(row, 'Approve')}
          >
            <Check className="h-3.5 w-3.5" /> Approve
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={busyId === row.workflowInstanceId}
            onClick={() => act(row, 'Reject')}
          >
            <X className="h-3.5 w-3.5" /> Reject
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={busyId === row.workflowInstanceId}
            onClick={() => act(row, 'Comment')}
          >
            <MessageSquare className="h-3.5 w-3.5" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="-m-6 p-[3px]">
      {err && (
        <Alert variant="danger" className="mb-3">
          {err}
        </Alert>
      )}
      <Card>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-content-primary">Approval inbox</h2>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
        <DataGrid<WorkflowInstance>
          data={rows}
          columns={columns}
          defaultPageSize={10}
          pageSizes={[5, 10, 25, 50]}
        />
      </Card>
    </div>
  );
}
